import { ActionTree } from "vuex";
import { KycVerificationStatus } from "@scrile/api-provider/dist/api/KycPropertiesProvider";
import { User } from "@scrile/api-provider/dist/api/UserProvider";
import { UserVerificationStatus } from "@scrile/api-provider/dist/api/UserVerificationProvider";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import { ACTION_TYPES, MUTATION_TYPES } from "@/store/const";
import providers, { getManagerProviders } from "@/lib/api";
import { LoginData } from "@/lib/authHelper";
import { isUserUnderAge } from "@/lib/parentalConsentHelpers";
import payments from "@/payments";
import config from "@/config";
import { StateRoot } from "./state";

export const actions: ActionTree<StateRoot, StateRoot> = {
  async [ACTION_TYPES.LOGIN](context, data: LoginData) {
    context.commit(MUTATION_TYPES.SET_LOGIN_DATA, data);
    await Promise.all([
      context.dispatch(ACTION_TYPES.GET_VERIFICATION_STATUS, data.user),
      context.dispatch(ACTION_TYPES.GET_IDENTITY_STATUS, { userId: data.user.id }),
    ]);

    if (checkAuthorities(data.authorities ?? [], ["getMyPersonalInfo"])) {
      await context.dispatch(ACTION_TYPES.GET_PERSONAL_INFO, data.user);
    }

    if (isUserUnderAge(data.user.personalInfo)) {
      await context.dispatch(ACTION_TYPES.GET_PARENTAL_CONSENT, { userId: data.user.id });
    }
  },
  async [ACTION_TYPES.LOGOUT](context) {
    await providers.LoginProvider.logout();
    await getManagerProviders().providers.LoginProvider.setLongLiveToken("");
    context.commit(MUTATION_TYPES.LOGOUT);
  },
  async [ACTION_TYPES.GET_VERIFICATION_STATUS](context, user: User) {
    const userVerification = await providers.UserVerificationProvider.search({ data: { userIds: [user.id] } });
    let dataVerified = userVerification.result.find(i => i.userId === user.id);
    context.commit(
      MUTATION_TYPES.SET_USER_VERIFICATION_STATUS,
      dataVerified?.status ?? UserVerificationStatus.UNVERIFIED
    );
  },
  async [ACTION_TYPES.GET_PERSONAL_INFO](context, user: User) {
    const personalInfo = await providers.PersonalInformationProvider.find({ id: user.id });
    context.commit(MUTATION_TYPES.SET_USER, { ...user, personalInfo: personalInfo ?? null });
  },
  async [ACTION_TYPES.GET_IDENTITY_STATUS](context, payload: { userId: string }) {
    if (!config.featureKycVerification) return;
    const identityStatus = (
      await providers.KycPropertiesProvider.getProperties({ data: { userId: payload.userId } })
    )[0].status;
    context.commit(MUTATION_TYPES.SET_IDENTITY_STATUS, identityStatus ?? KycVerificationStatus.DEFAULT);
  },

  async [ACTION_TYPES.GET_PARENTAL_CONSENT](context, payload: { userId: string }) {
    const parentalConsent = await providers.ParentalConsentProvider.search({ userId: payload.userId });
    context.commit(MUTATION_TYPES.SET_PARENTAL_CONSENT, parentalConsent);
  },

  async [ACTION_TYPES.GET_SYSTEM_SETTINGS](context) {
    const [systemSettings, currentPaymentSystem] = await Promise.all([
      providers.SystemSettingsProvider.get(),
      providers.PaymentSystemsProvider.findForCreditCardAssignment(),
    ]);
    context.commit(MUTATION_TYPES.SET_STATE, {
      systemSettings,
      currentPaymentSystem,
      creditCardAllowed: payments.checkAllowStoreCard(currentPaymentSystem),
    });
    return systemSettings;
  },
};
